import type { Ref } from 'vue'
import type { Customer, CustomerAddress } from '@/types/customer'
import type { Order as OrderType } from '@/services/orderService'
import type { OrderPerson } from '@/composables/useMultipleDiners'
import type { useOrderCreation } from '@/composables/useOrderCreation'
import type { CustomerPrintOrder } from '@/utils/customerPrintBuilders'
import { validateOrderForm } from '@/utils/orderFormHelpers'

type PaymentMethod = 'cash' | 'card' | 'digital' | 'other'

type NewOrderForm = {
  type: 'Dine-in' | 'Takeaway' | 'Delivery'
  tableId: number | string | null
  customerName: string
  notes: string
  items: Array<{
    menu_item_id: number
    variant_id?: number | null
    quantity: number
    notes?: string
    special_instructions?: string
    unit_price?: number
    extras?: Array<{ name: string; price: number; quantity: number }>
  }>
}

interface UseOrderSubmissionFlowOptions {
  form: Ref<NewOrderForm>
  persons: Ref<OrderPerson[]>
  isEditMode: Ref<boolean>
  orderToEdit: Ref<OrderType | null | undefined>
  posMode: Ref<boolean>
  currentSession: Ref<unknown>
  markAsPaid: Ref<boolean>
  selectedPaymentMethod: Ref<PaymentMethod>
  cashReceived: Ref<number>
  ivaEnabled: Ref<boolean>
  ivaRate: Ref<number>
  deliveryFee: Ref<number>
  orderTotal: Ref<number>
  currentDiscount: Ref<number>
  appliedPromotions: Ref<any[]>
  selectedCustomer: Ref<Customer | null>
  selectedAddress: Ref<CustomerAddress | null>
  allowDineInWithoutTable: Ref<boolean | undefined>
  allowTakeawayWithoutCustomer: Ref<boolean | undefined>
  orderCreationComposable: ReturnType<typeof useOrderCreation>
  t: (key: string) => string
  showError: (message: string) => void
  showSuccess: (message: string) => void
  showWarning: (message: string, duration?: number) => void
  ensureRestaurantLoaded: () => Promise<void>
  isKitchenPrintEnabled: () => boolean
  isCustomerPrintEnabled: () => boolean
  printKitchenTicket: (order: OrderType) => Promise<void>
  printCustomerReceipt: (order: CustomerPrintOrder) => Promise<void>
  waitForUi: () => Promise<void>
  emitOrderUpdated: (order: OrderType) => void
  emitOrderCreated: (order: OrderType) => void
  emitClose: () => void
  resetForNextSale: () => void
}

function buildNewOrderData(
  form: NewOrderForm,
  selectedCustomer: Customer | null,
  selectedAddress: CustomerAddress | null
): {
  type: NewOrderForm['type']
  tableId: number | string | null
  customerName: string
  notes: string
  customer_data?: {
    name: string
    phone: string
    address?: string
    apartment?: string
    neighborhood?: string
    city?: string
    postal_code?: string
    reference_point?: string
    notes?: string
    address_id: number | null
  }
} {
  const orderData = {
    type: form.type,
    tableId: form.tableId,
    customerName: form.customerName,
    notes: form.notes
  }

  // Include customer data for both Delivery and Takeaway orders
  if ((form.type === 'Delivery' || form.type === 'Takeaway') && selectedCustomer) {
    return {
      ...orderData,
      customer_data: {
        name: selectedCustomer.name,
        phone: selectedCustomer.phone,
        address: selectedAddress?.address || selectedCustomer.address,
        apartment: selectedAddress?.apartment || selectedCustomer.apartment,
        neighborhood: selectedAddress?.neighborhood || selectedCustomer.neighborhood,
        city: selectedAddress?.city || selectedCustomer.city,
        postal_code: selectedAddress?.postal_code || selectedCustomer.postal_code,
        reference_point: selectedAddress?.reference_point || selectedCustomer.reference_point,
        notes: selectedCustomer.notes,
        address_id: selectedAddress?.id || null
      }
    }
  }

  return orderData
}

export function useOrderSubmissionFlow(options: UseOrderSubmissionFlowOptions) {
  const collectValidItems = () => {
    const validItems: OrderPerson['items'] = []

    options.persons.value.forEach((person) => {
      person.items.forEach((item) => {
        if (item.quantity > 0) {
          validItems.push(item)
        }
      })
    })

    return validItems
  }

  const submitOrder = async () => {
    try {
      if (options.posMode.value && options.markAsPaid.value && !options.currentSession.value) {
        options.showError('No hay una sesión de caja abierta. Por favor abre una sesión de caja antes de procesar pagos.')
        return
      }

      const validItems = collectValidItems()
      const hasItems = options.orderCreationComposable.validateHasItems(
        true,
        options.persons.value,
        validItems
      )

      if (!hasItems) {
        options.showError(options.t('app.views.orders.modals.new_order.errors.add_one_item'))
        return
      }

      if (options.isEditMode.value && options.orderToEdit.value) {
        const updated = await options.orderCreationComposable.updateExistingOrder(
          options.orderToEdit.value.id,
          options.form.value,
          options.orderToEdit.value,
          validItems,
          options.persons.value,
          options.ivaEnabled.value,
          options.ivaRate.value,
          options.selectedCustomer.value,
          options.selectedAddress.value,
          options.deliveryFee.value,
          options.markAsPaid.value,
          options.selectedPaymentMethod.value,
          options.cashReceived.value,
          options.currentDiscount.value,
          options.appliedPromotions.value
        )

        options.emitOrderUpdated(updated)
        options.emitClose()
        return
      }

      const validation = validateOrderForm(
        options.form.value,
        hasItems,
        options.allowDineInWithoutTable.value,
        options.allowTakeawayWithoutCustomer.value
      )

      if (!validation.isValid) {
        validation.errors.forEach((error) => options.showError(error))
        return
      }

      const orderData = buildNewOrderData(
        options.form.value,
        options.selectedCustomer.value,
        options.selectedAddress.value
      )

      const { order, paymentSuccess } = await options.orderCreationComposable.createNewOrder(
        orderData,
        true,
        options.persons.value,
        validItems,
        options.markAsPaid.value,
        options.selectedPaymentMethod.value,
        options.cashReceived.value,
        options.orderTotal.value,
        options.t,
        options.showSuccess,
        options.showWarning,
        options.showError,
        options.posMode.value,
        options.ivaEnabled.value,
        options.ivaRate.value,
        options.deliveryFee.value,
        options.currentDiscount.value,
        options.appliedPromotions.value
      )

      await options.ensureRestaurantLoaded()

      if (options.isKitchenPrintEnabled()) {
        try {
          await options.waitForUi()
          await options.printKitchenTicket(order)
        } catch {
          options.showWarning(options.t('app.views.orders.modals.new_order.print_warning'))
        }
      }

      if (
        options.isCustomerPrintEnabled() &&
        options.posMode.value &&
        options.markAsPaid.value &&
        paymentSuccess
      ) {
        try {
          await options.waitForUi()

          const orderWithPayment: CustomerPrintOrder = {
            ...order,
            payment_method: options.selectedPaymentMethod.value,
            amount_paid: options.selectedPaymentMethod.value === 'cash' ? options.cashReceived.value : undefined
          }

          await options.printCustomerReceipt(orderWithPayment)
        } catch {
          options.showWarning(options.t('app.views.orders.modals.new_order.print_warning'))
        }
      }

      options.emitOrderCreated(order)

      if (options.posMode.value) {
        await options.waitForUi()
        options.resetForNextSale()
      } else {
        options.emitClose()
      }
    } catch (error) {
      if (!(error instanceof Error && error.message === 'VALIDATION_ERROR')) {
        options.showError(options.t('app.views.orders.modals.new_order.errors.create_failed'))
      }
    }
  }

  return {
    submitOrder
  }
}
